import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { inject, provideAppInitializer, provideZoneChangeDetection, Injector, runInInjectionContext } from '@angular/core';
import { AutoRefreshTokenService, createInterceptorCondition, INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG, IncludeBearerTokenCondition, includeBearerTokenInterceptor, provideKeycloak, ProvideKeycloakOptions, UserActivityService, withAutoRefreshToken } from 'keycloak-angular';
import { ZopAuthConfigService } from './providers/zop-auth-config.provider';
import { KeycloakSessionSyncService } from './services/keycloak-session/keycloak-session-sync.service';

function createUrlConditions(injector: Injector): IncludeBearerTokenCondition[] {
  try {
    return runInInjectionContext(injector, () => {
      const authConfig = inject(ZopAuthConfigService);
      const config = authConfig.getConfig();

      const conditions: IncludeBearerTokenCondition[] = [
        // Always include localhost for development
        createInterceptorCondition<IncludeBearerTokenCondition>({
          urlPattern: /^https:\/\/localhost(:\d+)?(\/.*)?$/i,
          bearerPrefix: 'Bearer'
        })
      ];

      // Add configured API base URL
      if (config.api.baseUrl) {
        conditions.push(createInterceptorCondition<IncludeBearerTokenCondition>({
          urlPattern: new RegExp(`^${config.api.baseUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\/.*)?$`, 'i'),
          bearerPrefix: 'Bearer'
        }));
      }

      // Add additional endpoints if configured
      if (config.api.endpoints && Object.keys(config.api.endpoints).length > 0) {
        Object.values(config.api.endpoints).forEach(endpoint => {
          conditions.push(createInterceptorCondition<IncludeBearerTokenCondition>({
            urlPattern: new RegExp(`^${endpoint.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\/.*)?$`, 'i'),
            bearerPrefix: 'Bearer'
          }));
        });
      }

      return conditions;
    });
  } catch (error) {
    console.error(error);
    // Fallback to default conditions if config service is not available
    return getDefaultUrlConditions();
  }
}

// Default fallback conditions for backward compatibility
function getDefaultUrlConditions(): IncludeBearerTokenCondition[] {
  return [
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^https:\/\/localhost(:\d+)?(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    // Replace the URL with the correct one once the gateway is ready
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-api-gateway.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-audit-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-brands-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-companies-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-contacts-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-currencies-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-customers-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-documents-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-finance-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-infrastructure-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-inventory-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-locations-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-product-catalogue-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-sales-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-scheduler-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-settings-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-subscriptions-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-tasks-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-tax-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    }),
    createInterceptorCondition<IncludeBearerTokenCondition>({
      urlPattern: /^(https:\/\/zopup-users-api.proudpond-04edd507.centralus.azurecontainerapps.io)(\/.*)?$/i,
      bearerPrefix: 'Bearer'
    })
  ];
}

export function provideZopKeycloakAuth(options: ProvideKeycloakOptions & { excludeHttpClient?: boolean }) {
  const { excludeHttpClient = false, ...keycloakOptions } = options;

  // Use caller-provided features if explicitly set (including empty array to disable),
  // otherwise fall back to the default withAutoRefreshToken setup.
  // This lets app.config.ts pass `features: []` in dev to disable auto-refresh,
  // or pass a custom features array without it being silently overridden.
  const resolvedFeatures = keycloakOptions.features !== undefined
    ? keycloakOptions.features
    : [
        withAutoRefreshToken({
          onInactivityTimeout: 'logout',
          sessionTimeout: 900000, // 15 minutes
        }),
      ];

  // When using onLoad: 'login-required', silentCheckSsoRedirectUri has no effect and
  // actively causes Keycloak initialisation to fail: the silent-sso iframe performs an
  // XHR to the Keycloak token endpoint which is blocked by CORS in local/dev environments.
  // Strip it automatically so misconfigured callers don't break.
  const resolvedInitOptions = { ...keycloakOptions.initOptions };
  if (resolvedInitOptions.onLoad === 'login-required') {
    delete (resolvedInitOptions as any).silentCheckSsoRedirectUri;
  }

  const providers = [
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Ensure centralized session handling is activated as early as possible.
    provideAppInitializer(() => {
      inject(KeycloakSessionSyncService);
    }),
    provideKeycloak({
      ...keycloakOptions,
      initOptions: resolvedInitOptions,

      features: resolvedFeatures,
      providers: [
        AutoRefreshTokenService,
        UserActivityService,
        {
          provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
          useFactory: (injector: Injector) => {
            try {
              return createUrlConditions(injector);
            } catch {
              // Fallback to default conditions if configuration service is not available
              return getDefaultUrlConditions();
            }
          },
          deps: [Injector],
        },
      ],
    }),
  ];

  // Only add HTTP client if not excluded
  if (!excludeHttpClient) {
    providers.push(provideHttpClient(withInterceptors([includeBearerTokenInterceptor])));
  }

  return providers;
}
